import React, { useEffect, useState } from 'react';
import marked from 'marked';
import { Grid, Form, Segment, Button, Message } from 'semantic-ui-react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { Formik } from 'formik';

import { filterPropsType } from '../../../types';
import FiltersPanelInput from './FiltersPanelInput';
import ButtonWrapper from '../../common/ButtonWrapper';
import { wrapConsumer } from '../FilteringContextProvider';
import { AccordionWrapper } from 'react-lib/dist/components-es';
import GridColumnInputWrapper from '../../common/GridColumnInputWrapper';
import { generateQueryDSLQuery } from '../QueryGenerators/JavaQueryBuilder';
import CommonMultipleSearchSelectionWrapper from '../../common/CommonMultipleSearchSelectionWrapper';

import {
  defaultFilterColumn,
  FILTER_EMPTY_PLACEHOLDER,
  FILTER_NOT_EMPTY_PLACEHOLDER,
  filterFieldSuffix,
  userDefaultFilterPropHiddenValue,
} from '../../../constants';
import { history } from '../../../store';
import { sharedDataViewToFilterState } from '../../../utils';
import { compose } from 'redux';
import { connect } from 'react-redux';

const FiltersPanel = ({
  handleFilterStateChanged,
  handleApplyFiltering,
  showingFilters,
  filterProps,
  setVisibleFields,
  numberOfGridColumns,
  showOptions,
  filterState,
  setSelectedRows,
  updateMultipleSelectValues,
  showPersonalData,
  t,
}) => {
  const formikRef = React.useRef(null);
  const getSearchQuery = () => {
    if (filterState.filterValues && Object.values(filterState.filterValues).length > 0) {
      return generateQueryDSLQuery(
        filterState.filterValues,
        filterProps.possibleFieldsList,
        filterState.filterLogicalOperator
      ).searchReadable;
    }
  };
  const [dataViewValues, setDataViewValues] = useState({});
  const [isLoadedDataViewValues, setIsLoadedDataViewValues] = useState(false);
  const { sharedDataView } = filterState;

  useEffect(() => {
    setDataViewValues(filterState.filterValues);
    setIsLoadedDataViewValues(true);
    document.addEventListener('keydown', onEnterPress, false);
  }, []);

  useEffect(() => {
    if (sharedDataView && Object.keys(sharedDataView).length > 0) {
      handleFilterStateChanged(sharedDataViewToFilterState(sharedDataView, filterProps));
    }
  }, [sharedDataView]);

  const applyFilter = formikProps => {
    let formValues = formikProps.values;
    if (!showPersonalData) {
      Object.values(formValues).forEach(val => {
        if (Array.isArray(val)) {
          val.forEach(v => {
            v.value = v.privateValue;
          });
        }
      });
    }
    Object.filter = (obj, predicate) =>
      Object.assign(
        ...Object.keys(obj)
          .filter(key => predicate(obj[key]))
          .map(key => ({ [key]: obj[key] }))
      );
    //since we don't have access to the same formikProps from OverviewTableHeader, we are setting empty/non-empty values here

    let hasEmptyFilters =
      filterState.filterValues &&
      (Object.values(filterState.filterValues).includes(FILTER_EMPTY_PLACEHOLDER) ||
        Object.values(filterState.filterValues).includes(FILTER_NOT_EMPTY_PLACEHOLDER));
    let hasDeletedFilters = filterState.filterValues && filterState.filterValues.HIST_IS_DELETED;

    if (hasEmptyFilters || hasDeletedFilters) {
      let filteredEmptyValue = {};
      if (hasEmptyFilters)
        filteredEmptyValue = Object.filter(
          filterState.filterValues,
          val => val === FILTER_EMPTY_PLACEHOLDER || val === FILTER_NOT_EMPTY_PLACEHOLDER
        );

      //In case if we apply date or numeric filters, we need to remove '<EMPTY>' or '<NOT EMPTY>' filters.
      //We need to do this operation because field key are not matching: GEN_CHECK_DATE_LESS_THAN and GEN_CHECK_DATE is the same field
      Object.keys(filteredEmptyValue).forEach(emptyVal => {
        if (
          Object.keys(formValues).includes(emptyVal + filterFieldSuffix.more_than) ||
          Object.keys(formValues).includes(emptyVal + filterFieldSuffix.less_than)
        ) {
          delete filteredEmptyValue[emptyVal];
          delete formValues[emptyVal];
        }
      });

      let filteredDeletedValue = {};
      if (hasDeletedFilters) {
        let { HIST_IS_DELETED } = filterState.filterValues;
        filteredDeletedValue = { HIST_IS_DELETED };
      }

      let manipulatedValues = {
        ...filteredDeletedValue,
        ...filteredEmptyValue,
        ...formValues,
      };

      formikProps.setValues(manipulatedValues);
      handleApplyFiltering(manipulatedValues);
    } else {
      handleApplyFiltering(formValues);
    }
    setSelectedRows({});
  };

  const onEnterPress = e => {
    e.stopPropagation();
    if (e.keyCode === 13 || e.key === 'Enter') {
      //This timeout is needed probably because of debouncing in the InputWrapper
      setTimeout(function () {
        if (formikRef && formikRef.current !== null) {
          handleApplyFiltering(formikRef.current.state.values);
        }
      }, 500);
    }
  };

  const renderFilteringOption = formikProps => (
    <>
      <div className="noselect">
        {t('inventory.overview.page.filter.container.filter.by.fields')}
      </div>
      <Grid stackable>
        <Grid.Row columns={2}>
          <Grid.Column>
            <CommonMultipleSearchSelectionWrapper
              name="selectFilterFieldsSelect"
              optionsList={filterProps.possibleFieldsList.filter(
                f => f.parentColumnTypeName !== 'GENERAL_EXCLUDED_FILTER'
              )}
              values={filterProps.visibleFieldsList}
              onChange={newVisibleFields => {
                let valuesTmp = { ...formikProps.values, ...filterState.filterValues };
                let isRemovedValue = false;
                Object.keys(formikProps.values).forEach(entry => {
                  let entryWithoutSuffix = entry
                    .replace(filterFieldSuffix.more_than, '')
                    .replace(filterFieldSuffix.less_than, '');
                  //EMPTY/NOT EMPTY filters should not be removed, otherwise trade/chemical names and hazards are very complicate to synchronize
                  if (
                    !newVisibleFields.map(e => e.key).includes(entryWithoutSuffix) &&
                    ![FILTER_EMPTY_PLACEHOLDER, FILTER_NOT_EMPTY_PLACEHOLDER].includes(
                      valuesTmp[entry]
                    )
                  ) {
                    delete valuesTmp[entry];
                    isRemovedValue = true;
                  }
                });
                setDataViewValues(valuesTmp);
                formikProps.setValues(valuesTmp);
                setVisibleFields(newVisibleFields, valuesTmp);

                if (isRemovedValue) {
                  handleApplyFiltering(valuesTmp);
                }
              }}
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
      <div style={{ marginTop: '5px' }} className="noselect">
        {t('inventory.overview.page.filter.container.logical.operator')}
      </div>
      <div style={{ marginTop: '2px' }}>
        <Button.Group size={'mini'}>
          <Button
            positive={filterState.filterLogicalOperator === 'AND'}
            onClick={() => {
              handleFilterStateChanged({ filterLogicalOperator: 'AND' });
              setSelectedRows({});
            }}
          >
            AND
          </Button>
          <Button.Or style={{ zIndex: 1 }} />
          <Button
            positive={filterState.filterLogicalOperator === 'OR'}
            onClick={() => {
              handleFilterStateChanged({ filterLogicalOperator: 'OR' });
              setSelectedRows({});
            }}
          >
            OR
          </Button>
        </Button.Group>
      </div>
      <div style={{ marginTop: '5px' }} className="noselect">
        {t('inventory.overview.page.filter.container.filter.used')}
      </div>
      <div style={{ marginTop: '2px' }}>
        <Message color={'yellow'} style={{ padding: '5px 10px 5px 10px' }}>
          <div dangerouslySetInnerHTML={getFilterHtmlObject()} />
        </Message>
      </div>
    </>
  );

  const getFilterHtmlObject = () => {
    let searchQuery = getSearchQuery();
    return {
      __html: getFilterHtml(
        searchQuery && typeof searchQuery !== 'undefined'
          ? searchQuery
          : t('inventory.overview.page.filter.container.no.filter')
      ),
    };
  };

  const getFilterHtml = text => {
    let tokens = marked.lexer(text);
    return marked.parser(tokens);
  };
  return (
    isLoadedDataViewValues && (
      <Formik
        ref={formikRef}
        initialValues={{}}
        validationOnChange={false}
        validationOnBlur={false}
        render={formikProps => (
          <Grid stackable>
            <Grid.Row
              columns={numberOfGridColumns}
              style={{ padding: '5px 5px 5px 5px' }}
              className="smallScreenReduceTopBottomRowPadding"
            >
              {showingFilters.visibleFields.map(field => {
                if (field) {
                  if (filterState.filterValues != null) {
                    setDataViewValues(filterState.filterValues);
                  }
                  return (
                    <GridColumnInputWrapper
                      errors={formikProps.errors}
                      value={formikProps.values[field.key] || ''}
                      key={field.key}
                      name={field.key}
                      label={field.label}
                      handleChange={formikProps.handleChange}
                      type="text"
                      labelClassName="noselect"
                      render={() => (
                        <FiltersPanelInput
                          field={field}
                          formikProps={formikProps}
                          multipleSelectOptions={showingFilters.multipleSelectOptions}
                          multipleSelectValues={showingFilters.multipleSelectValues}
                          updateMultipleSelectValues={updateMultipleSelectValues}
                          dataViewValues={dataViewValues || filterState.filterValues || {}}
                          setDataViewValues={setDataViewValues}
                        />
                      )}
                    />
                  );
                }
              })}
            </Grid.Row>
            <Grid.Row
              columns={showOptions ? 2 : 1}
              style={{ padding: '5px 5px 10px 5px' }}
              className="smallScreenReduceTopBottomRowPadding"
            >
              {showOptions && (
                <Grid.Column>
                  <Form.Field>
                    <Segment basic className="notPaddedSegment filterSettings">
                      <AccordionWrapper
                        accordionTitle={t('inventory.overview.page.filter.container.settings')}
                        className="accordionNoBorder"
                        icon="filter"
                        collapsed={true}
                        render={() => {
                          return renderFilteringOption(formikProps);
                        }}
                      />
                    </Segment>
                  </Form.Field>
                </Grid.Column>
              )}
              <Grid.Column floated="right" verticalAlign="bottom">
                <Segment basic className="notPaddedSegment" floated="right">
                  <ButtonWrapper
                    type="button"
                    icon="close"
                    label="inventory.overview.page.filter.container.clean.button"
                    onClick={() => {
                      history.push({
                        state: {
                          userDefaultFilterProp: userDefaultFilterPropHiddenValue[
                            filterState.userDefaultFilterProp
                          ]
                            ? {
                                [filterState.userDefaultFilterProp]:
                                  userDefaultFilterPropHiddenValue[
                                    defaultFilterColumn.HIST_IS_DELETED
                                  ],
                              }
                            : undefined,
                        },
                      });

                      Object.keys(showingFilters.multipleSelectValues).forEach(k => {
                        showingFilters.multipleSelectValues[k] = [];
                      });
                      formikProps.resetForm();
                      handleApplyFiltering(
                        userDefaultFilterPropHiddenValue[filterState.userDefaultFilterProp]
                          ? {
                              [filterState.userDefaultFilterProp]:
                                userDefaultFilterPropHiddenValue[
                                  defaultFilterColumn.HIST_IS_DELETED
                                ],
                            }
                          : {}
                      );
                      setSelectedRows({});
                    }}
                  />
                  <ButtonWrapper
                    type="button"
                    icon="search"
                    className="mini marginRightButton"
                    label="inventory.overview.page.filter.container.apply.button"
                    color={'blue'}
                    onClick={() => {
                      applyFilter(formikProps);
                    }}
                  />
                </Segment>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        )}
      />
    )
  );
};

FiltersPanel.propTypes = {
  filterProps: filterPropsType.isRequired,
  handleApplyFiltering: PropTypes.func.isRequired,
  showingFilters: PropTypes.shape({
    multipleSelectOptions: PropTypes.object.isRequired,
    multipleSelectValues: PropTypes.object.isRequired,
    visibleFields: PropTypes.array.isRequired,
    visibleFieldsOriginal: PropTypes.array.isRequired,
  }),
  setVisibleFields: PropTypes.func.isRequired,
};

FiltersPanel.defaultProps = {
  numberOfGridColumns: 4,
  showOptions: true,
};

const mapStateToProps = state => ({
  showPersonalData: state.dataPrivacy.showPersonalData,
});

export default compose(
  withTranslation(),
  connect(mapStateToProps, null)
)(wrapConsumer(FiltersPanel));
